import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Alert, Spin, Segmented } from "antd";
import { LiveDataStatus } from "../../../hooks/useLiveDataSession";
import { useLiveDataHistory, LiveDataHistoryPoint } from "../../../hooks/useLiveDataHistory";
import "./liveGraph.scss";

/** Circulator: ENABLED=100, DISABLED=0 (top/bottom band) */
const mapCirculatorToNumber = (state?: string): number | null => {
  if (!state) return null;
  const upper = state.toUpperCase();
  if (upper === "ENABLED" || upper === "OPEN" || upper === "ON") return 100;
  if (upper === "DISABLED" || upper === "CLOSED" || upper === "OFF") return 0;
  return null;
};

/** Valve: ENABLED=80, DISABLED=20 (middle band - distinct from circulator so both visible) */
const mapValveToNumber = (state?: string): number | null => {
  if (!state) return null;
  const upper = state.toUpperCase();
  if (upper === "ENABLED" || upper === "OPEN" || upper === "ON") return 80;
  if (upper === "DISABLED" || upper === "CLOSED" || upper === "OFF") return 20;
  return null;
};

const formatTime = (timestamp: number) => {
  const d = new Date(timestamp * 1000);
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

interface ChartDataPoint {
  time: number;
  timestamp: number;
  power_soliseco?: number;
  voltage_soliseco?: number;
  water_temperature?: number;
  heating_percentage_power?: number;
  circulator_line?: number;
  valve_line?: number;
  circulator_state?: string;
  valve_state?: string;
}

type GraphSegment = "POWER" | "VOLTAGE" | "TEMPERATURE" | "STATES";

interface LiveGraphProps {
  liveDataStatus: LiveDataStatus | null;
}

const LiveGraph: React.FC<LiveGraphProps> = ({ liveDataStatus }) => {
  const { device_id } = useParams<{ device_id: string }>();
  const { t: tSettings } = useTranslation("settings");
  const { t: tDashboard } = useTranslation("dashboard");
  const [segment, setSegment] = useState<GraphSegment>("POWER");

  const isLiveDataActive = liveDataStatus?.live_data_enable || false;

  const { data, loading, error } = useLiveDataHistory({
    deviceId: device_id,
    enabled: isLiveDataActive,
    hours: 2,
    frequency: 15000,
  });

  const chartData = useMemo((): ChartDataPoint[] => {
    return data.map((p: LiveDataHistoryPoint) => ({
      time: p.timestamp,
      timestamp: p.timestamp,
      power_soliseco: p.power_soliseco,
      voltage_soliseco: p.voltage_soliseco,
      water_temperature: p.water_temperature,
      heating_percentage_power: p.heating_percentage_power,
      circulator_line: mapCirculatorToNumber(p.circulator_state) ?? undefined,
      valve_line: mapValveToNumber(p.valve_state) ?? undefined,
      circulator_state: p.circulator_state,
      valve_state: p.valve_state,
    }));
  }, [data]);

  const formatTooltipValue = (dataKey: string, value: number | undefined, payload?: any): string => {
    if (dataKey === "circulator_line") return payload?.circulator_state ?? (value != null && value >= 50 ? "ENABLED" : "DISABLED");
    if (dataKey === "valve_line") return payload?.valve_state ?? (value != null && value >= 50 ? "ENABLED" : "DISABLED");
    if (value == null) return "";
    if (dataKey === "water_temperature") return `${value.toFixed(1)} °C`;
    if (dataKey === "power_soliseco") return `${value} W`;
    if (dataKey === "voltage_soliseco") return `${value} V`;
    if (dataKey === "heating_percentage_power") return `${value} %`;
    return String(value);
  };

  const renderTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="live-graph-tooltip">
        <p style={{ fontWeight: 700, marginBottom: 4 }}>{formatTime(label)}</p>
        {payload.map((entry: any) => {
          const value = entry.payload?.[entry.dataKey];
          if (value == null && (entry.dataKey === "circulator_line" || entry.dataKey === "valve_line")) return null;
          const displayValue = formatTooltipValue(entry.dataKey, value, entry.payload);
          if (!displayValue && entry.dataKey !== "circulator_line" && entry.dataKey !== "valve_line") return null;
          return (
            <p key={entry.dataKey} style={{ color: entry.stroke || entry.color, fontWeight: 500 }}>
              {entry.name}: {displayValue}
            </p>
          );
        })}
      </div>
    );
  };

  const renderChart = () => {
    if (segment === "POWER") {
      return (
        <LineChart data={chartData} margin={{ top: 5, right: 50, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="time" type="number" domain={["dataMin", "dataMax"]} tickFormatter={(ts) => formatTime(ts)} />
          <YAxis yAxisId="left" orientation="left" unit="W" tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : String(v))} />
          <YAxis yAxisId="right" orientation="right" width={45} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
          <Tooltip content={renderTooltip} />
          <Legend />
          <Line yAxisId="left" type="monotone" dataKey="power_soliseco" name={tDashboard("last_info_power")} stroke="#82BA71" dot={false} connectNulls />
          <Line yAxisId="right" type="monotone" dataKey="heating_percentage_power" name={tDashboard("last_info_power_percentage")} stroke="#D0021B" dot={false} connectNulls />
        </LineChart>
      );
    }

    if (segment === "VOLTAGE") {
      return (
        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="time" type="number" domain={["dataMin", "dataMax"]} tickFormatter={(ts) => formatTime(ts)} />
          <YAxis unit="V" />
          <Tooltip content={renderTooltip} />
          <Legend />
          <Line type="monotone" dataKey="voltage_soliseco" name={tDashboard("last_info_voltage_soliseco")} stroke="#4A90E2" dot={false} connectNulls />
        </LineChart>
      );
    }

    if (segment === "TEMPERATURE") {
      return (
        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="time" type="number" domain={["dataMin", "dataMax"]} tickFormatter={(ts) => formatTime(ts)} />
          <YAxis unit="°C" domain={["auto", "auto"]} />
          <Tooltip content={renderTooltip} />
          <Legend />
          <Line type="monotone" dataKey="water_temperature" name={tDashboard("last_info_current_temp")} stroke="#F5A623" dot={false} connectNulls />
        </LineChart>
      );
    }

    if (segment === "STATES") {
      return (
        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="time" type="number" domain={["dataMin", "dataMax"]} tickFormatter={(ts) => formatTime(ts)} />
          <YAxis domain={[0, 100]} ticks={[0, 50, 100]} tickFormatter={(v) => (v === 100 ? "ENABLED" : v === 0 ? "DISABLED" : "")} />
          <Tooltip content={renderTooltip} />
          <Legend />
          <Line type="stepAfter" dataKey="circulator_line" name={tDashboard("last_info_circulator_state")} stroke="#9013FE" strokeWidth={2} dot={false} connectNulls />
          <Line type="stepAfter" dataKey="valve_line" name={tDashboard("last_info_valve_state")} stroke="#7ED321" strokeWidth={2} dot={false} connectNulls />
        </LineChart>
      );
    }

    return (
      <LineChart data={chartData} margin={{ top: 5, right: 50, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="time" type="number" domain={["dataMin", "dataMax"]} tickFormatter={(ts) => formatTime(ts)} />
        <YAxis yAxisId="left" orientation="left" unit="W" tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : String(v))} />
        <YAxis yAxisId="right" orientation="right" width={45} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
        <Tooltip content={renderTooltip} />
        <Legend />
        <Line yAxisId="left" type="monotone" dataKey="power_soliseco" name={tDashboard("last_info_power")} stroke="#82BA71" dot={false} connectNulls />
        <Line yAxisId="right" type="monotone" dataKey="heating_percentage_power" name={tDashboard("last_info_power_percentage")} stroke="#D0021B" dot={false} connectNulls />
      </LineChart>
    );
  };

  if (!isLiveDataActive && chartData.length === 0) {
    return (
      <div className="live-graph-container live-graph-inactive">
        <p className="hint">{tSettings("enable_live_data_hint") || "Enable live data to see the graph"}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="live-graph-container">
        <Alert message={tSettings("error") || "Error"} description={error} type="error" showIcon />
      </div>
    );
  }

  if (loading && chartData.length === 0) {
    return (
      <div className="live-graph-container live-graph-loading">
        <Spin size="large" />
        <p>{tSettings("loading") || "Loading..."}</p>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="live-graph-container live-graph-empty">
        <p>{tSettings("no_data_available") || "No data available"}</p>
        <p className="hint">Data will appear once the device sends live data.</p>
      </div>
    );
  }

  return (
    <div className="live-graph-container">
      <div className="live-graph-header">
        <span className="live-graph-title">{tSettings("live_data") || "Live Data"} — 2h</span>
        <div className="live-graph-tabs">
          <Segmented
            options={[
              { label: tDashboard("time_data_choice_powers"), value: "POWER" },
              { label: tDashboard("time_data_choice_tensions"), value: "VOLTAGE" },
              { label: tDashboard("time_data_choice_temperature"), value: "TEMPERATURE" },
              { label: tSettings("live_graph_states") || "States", value: "STATES" },
            ]}
            value={segment}
            onChange={(v) => setSegment(v as GraphSegment)}
            size="small"
            style={{ width: "fit-content", marginInline: "auto" }}
          />
        </div>
      </div>
      <div className="live-graph-chart">
        <ResponsiveContainer width="100%" height={300}>
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default LiveGraph;
